import { EVENTS } from 'constants/events';
import {
    TYPE_SEND_EMAIL,
    FEDERAL_OPTION,
    PENDO_ARIES_AGENT_ID,
    CC_TECH_SUPPORT_URL_PLACEHOLDER,
    DEFAULT_CC_TECH_SUPPORT_URL,
    ARIES_FALLBACK_CONTEXT_KEY,
} from 'constants/ai';

import { isEmpty } from 'ravenjs/utils/lodash';

import { AI_DIGITAL_ASSISTANT_ENABLED, ENABLE_CC_TECH_SUPPORT_URL } from 'constants/api';
import { convertStringToBoolean } from 'utils/common';
import { getMarkdownConverter } from 'utils/markdown';

export { getMarkdownConverter };

/**
 * Check whether the CC Tech Support URL feature flag is enabled.
 *
 * @method isCcTechSupportUrlEnabled
 * @return {boolean} True when the env-var flag is truthy.
 */
export const isCcTechSupportUrlEnabled = () => {
    return convertStringToBoolean(ENABLE_CC_TECH_SUPPORT_URL);
};

/**
 * Resolve the CC Tech Support URL within an answer.
 *
 * When the feature flag is enabled and a Zendesk submit URL is available, both
 * the placeholder and any literal occurrences of the default URL are replaced
 * with that submit URL; otherwise the placeholder resolves to the default URL.
 * Replacing the literal default URL handles historical chat messages that were
 * stored with the hardcoded URL baked in before the API returned the placeholder.
 * Answers without the placeholder or the default URL are returned unchanged.
 *
 * @method resolveCcTechSupportUrl
 * @param  {string} answer - The answer markdown that may contain the placeholder or default URL.
 * @param  {string} zendeskSubmitURL - Zendesk submit URL from redux.
 * @return {string} The answer with the CC tech support URL resolved.
 */
export const resolveCcTechSupportUrl = (answer, zendeskSubmitURL) => {
    if (
        !answer ||
        (!answer.includes(CC_TECH_SUPPORT_URL_PLACEHOLDER) &&
            !answer.includes(DEFAULT_CC_TECH_SUPPORT_URL))
    ) {
        return answer;
    }
    const url =
        isCcTechSupportUrlEnabled() && zendeskSubmitURL
            ? zendeskSubmitURL
            : DEFAULT_CC_TECH_SUPPORT_URL;
    const resolved = answer.split(CC_TECH_SUPPORT_URL_PLACEHOLDER).join(url);
    if (url === DEFAULT_CC_TECH_SUPPORT_URL) {
        return resolved;
    }
    return resolved.split(DEFAULT_CC_TECH_SUPPORT_URL).join(url);
};

export const clickReviewCompanyLink = sendPendoEvent => {
    const pendoEvent = EVENTS.AI_ASSISTANT.REVIEW_COMPANY_LOCATIONS;

    sendPendoEvent(pendoEvent);
};

export const clickAdditionalResource = (id, title, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.RESPONSE_LINK;
    pendoEvent.eventProperties.title = title;
    pendoEvent.eventProperties.responseID = id;

    sendPendoEvent(pendoEvent);
};

export const clickExternalLink = (externalUrl, sendPendoEvent) => {
    const { domain, sourceType, trustLevel } = externalUrl || {};
    const baseEvent = EVENTS.AI_ASSISTANT.EXTERNAL_LINK;
    const eventProperties = {
        ...baseEvent.eventProperties,
        domain: domain || '',
    };
    if (sourceType) {
        eventProperties.sourceType = sourceType;
    }
    if (trustLevel) {
        eventProperties.trustLevel = trustLevel;
    }

    sendPendoEvent({ event: baseEvent.event, eventProperties });
};

export const handlePendoAdditionalResources = (chat, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.RESPONSE_LINKS;
    pendoEvent.eventProperties.additionalResources = JSON.stringify(chat.additionalResources);
    pendoEvent.eventProperties.additionalResourcesFailed = JSON.stringify(
        chat.additionalResourcesFailed
    );

    sendPendoEvent(pendoEvent);
};

export const handlePendoAskButton = (message, sendPendoEvent) => {
    sendPendoEvent({
        event: EVENTS.AI_ASSISTANT.ASK_BUTTON.EVENT,
        eventProperties: {
            description: EVENTS.AI_ASSISTANT.ASK_BUTTON.EVENT_PROPERTIES.DESCRIPTION,
            message,
        },
    });
};

const toPendoId = value => (value === null || value === undefined ? value : String(value));

export const trackAriesAgentEvent = (action, { conversationId, messageId, content }) => {
    if (typeof window.pendo?.trackAgent === 'function') {
        window.pendo.trackAgent(action, {
            agentId: PENDO_ARIES_AGENT_ID,
            conversationId: toPendoId(conversationId),
            messageId: toPendoId(messageId),
            content,
        });
    }
};

/**
 * Determine whether a given fallbackType represents an ARIES fallback response.
 *
 * A response is treated as a fallback when a fallbackType is present and it is
 * not the "easy in scope" complexity type, which is handled as a real answer.
 *
 * @method isAriesFallback
 * @param  {string} fallbackType - The fallbackType returned on a chat response.
 * @return {boolean} True when the response should be treated as a fallback.
 */
export const isAriesFallback = fallbackType => {
    return Boolean(fallbackType) && fallbackType !== 'COMPLEXITY_IN_SCOPE_EASY';
};

/**
 * Persist the active ARIES fallback context in sessionStorage so downstream
 * post-fallback actions (search, expert) can be joined by conversationId.
 *
 * @method setAriesFallbackContext
 * @param  {Object} context - The context to persist.
 * @param  {string} context.conversationId - The active ARIES conversationId.
 */
export const setAriesFallbackContext = ({ conversationId } = {}) => {
    if (isEmpty(conversationId)) {
        return;
    }

    try {
        sessionStorage.setItem(
            ARIES_FALLBACK_CONTEXT_KEY,
            JSON.stringify({ conversationId, timestamp: Date.now() })
        );
    } catch (error) {
        // Ignore storage write failures (e.g. storage disabled/full)
    }
};

/**
 * Read the persisted ARIES fallback context from sessionStorage.
 *
 * @method getAriesFallbackContext
 * @return {Object|null} The stored context or null when absent/invalid.
 */
export const getAriesFallbackContext = () => {
    const stored = sessionStorage.getItem(ARIES_FALLBACK_CONTEXT_KEY);
    if (isEmpty(stored)) {
        return null;
    }

    try {
        const parsed = JSON.parse(stored);
        if (parsed && !isEmpty(parsed.conversationId)) {
            return parsed;
        }
    } catch (error) {
        // Ignore malformed context data
    }
    return null;
};

/**
 * Remove any persisted ARIES fallback context from sessionStorage.
 *
 * @method clearAriesFallbackContext
 */
export const clearAriesFallbackContext = () => {
    try {
        sessionStorage.removeItem(ARIES_FALLBACK_CONTEXT_KEY);
    } catch (error) {
        // Ignore storage removal failures
    }
};

export const sendEmailSession = async params => {
    const { chatSessionId, sendEmailCloseAISession, userId, loggedCompanyId } = params;

    if (!chatSessionId) {
        return false;
    }

    try {
        await sendEmailCloseAISession({
            params: {
                type: TYPE_SEND_EMAIL,
                companyId: loggedCompanyId,
                additionalInfo: {
                    chatSessionId,
                },
                userIds: [userId],
            },
        });

        if (!isEmpty(JSON.parse(localStorage.getItem('ai_chat_session')))) {
            localStorage.removeItem('ai_chat_session');
        }
        return Promise.resolve(true);
    } catch (e) {
        return Promise.reject(e);
    }
};

export const clickDefaultPrompt = (title, sendPendoEvent) => {
    const pendoEvent = EVENTS.AI_ASSISTANT.DEFAULT_PROMPT;
    pendoEvent.eventProperties.title = title;

    sendPendoEvent(pendoEvent);
};

/**
 * Check env var to enable AI Maintenance
 *
 * @method isAiDigitalAssistantEnabled
 * @param {string} valueTest
 * @return {boolean}
 */
export const isAiDigitalAssistantEnabled = valueTest => {
    const envValue =
        valueTest !== undefined
            ? valueTest
            : typeof AI_DIGITAL_ASSISTANT_ENABLED !== 'undefined'
            ? AI_DIGITAL_ASSISTANT_ENABLED
            : 'true';
    return String(envValue).toLowerCase() !== 'false';
};

export const getCreateFeedbackParams = ({ data, userId, loggedCompanyId }) => {
    const {
        feedbackAnswer = '',
        feedbackMessage = '',
        chatSessionId,
        chatMessageId,
        isHelpful,
    } = data;

    return {
        chatMessageId,
        chatSessionId,
        clientId: loggedCompanyId,
        reason: feedbackAnswer,
        isHelpful,
        detail: feedbackMessage,
        userId,
    };
};

/**
 * Determine whether a value is a non-empty array of verified resource links.
 *
 * @method hasVerifiedResources
 * @param  {*} verifiedResources - The value to check.
 * @return {boolean} True when the value is a non-empty array.
 */
export const hasVerifiedResources = verifiedResources => {
    return Array.isArray(verifiedResources) && verifiedResources.length > 0;
};

/**
 * Resolve the verified resource links for an answer, preferring the ones on the
 * response and falling back to the ones already stored on the message.
 *
 * @method resolveVerifiedResources
 * @param  {Object} response - The answer response.
 * @param  {Object} message  - The message the answer belongs to.
 * @return {Array} The verified resource links, or an empty array.
 */
export const resolveVerifiedResources = (response = {}, message = {}) => {
    if (hasVerifiedResources(response?.verifiedResources)) {
        return response.verifiedResources;
    }
    if (hasVerifiedResources(message?.verifiedResources)) {
        return message.verifiedResources;
    }
    return [];
};

export const saveHistoryChatInLocalStorage = historyChat => {
    let historyChatResult = historyChat;
    const lastSelectedStateIndex = historyChat
        .map(item => item.selectedState?.value !== undefined)
        .lastIndexOf(true);

    if (lastSelectedStateIndex !== -1) {
        historyChatResult = historyChat.slice(lastSelectedStateIndex);
    }

    localStorage.setItem('ai_chat_session', JSON.stringify(historyChatResult));
};

export const createChatHistoryMessages = (
    response,
    message,
    selectedState,
    zendeskSubmitURL = ''
) => {
    const { id: chatMessageId, answer = '', chatStatus, chatSessionId } = response;
    const resolvedAnswer = resolveCcTechSupportUrl(answer, zendeskSubmitURL);

    const userMessage = {
        id: `user-${chatMessageId}-${Date.now()}`,
        type: 'user',
        query: message,
    };

    const assistantMessage = {
        id: `msg-${chatMessageId}-${Date.now()}`,
        type: 'assistant',
        query: message,
        answer: resolvedAnswer,
        chatSessionId,
        chatStatus,
        chatMessageId,
        additionalResources: [],
        additionalResourcesFailed: [],
        chatResources: {},
        externalResources: [],
        verifiedResources: response.verifiedResources || [],
        verified: false,
        fallbackType: null,
        state: selectedState?.label || null,
        isAnimatingAnswer: true,
        startTime: Date.now(),
    };

    return { userMessage, assistantMessage };
};

export const updateHistoryChatWithFeedback = (historyChat, messageIndex, feedbackData) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        ...feedbackData,
    };
    return updated;
};

export const updateHistoryChatWithLikeDislike = (historyChat, messageIndex, type) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        likeDislike: type,
        waitingFeedback: true,
    };
    return updated;
};

export const cancelFeedback = (historyChat, messageIndex) => {
    const updated = [...historyChat];
    updated[messageIndex] = {
        ...updated[messageIndex],
        likeDislike: null,
        waitingFeedback: false,
    };
    return updated;
};

export const createStateChangeMessages = (previousState, newState, shouldAddEndConversation) => {
    const messages = [];

    if (shouldAddEndConversation) {
        messages.push({
            type: 'endConversation',
            previousSelectedState: previousState,
            id: `end-${Date.now()}`,
        });
    }

    messages.push({
        type: 'stateLabel',
        selectedState: newState,
        id: `state-${Date.now()}`,
    });

    return messages;
};

export const prepareSessionParams = ({
    selectedState,
    statesDataWithFederal,
    loggedCompanyId,
    userId,
    hasAdvisorSku,
}) => {
    const selectedStateData = selectedState ? selectedState.value : null;
    const stateData = statesDataWithFederal.find(state => state.stateCode === selectedStateData);
    const employeeCountData = stateData?.employeeCount || 0;
    const federalEntry = statesDataWithFederal.find(state => state.state === FEDERAL_OPTION.name);
    const totalEmployeeCount = federalEntry?.employeeCount || 0;

    return {
        clientId: loggedCompanyId,
        employeeCount: employeeCountData,
        hasAdvisorSku,
        state: selectedStateData,
        userId,
        totalEmployeeCount,
    };
};

export const updateMessageWithAnswer = (
    historyChat,
    messageIndex,
    response,
    converter,
    zendeskSubmitURL = ''
) => {
    const updated = [...historyChat];
    if (updated[messageIndex]) {
        const resolvedAnswer = resolveCcTechSupportUrl(response.answer, zendeskSubmitURL);
        const verifiedResources = resolveVerifiedResources(response, updated[messageIndex]);
        const useVerifiedResources = hasVerifiedResources(verifiedResources);
        updated[messageIndex].answer = converter.makeHtml(resolvedAnswer);
        updated[messageIndex].chatStatus = response.chatStatus;
        updated[messageIndex].resourceType = response.resourceType || null;
        updated[messageIndex].resources = useVerifiedResources ? null : response.resources || null;
        updated[messageIndex].chatResources = useVerifiedResources ? {} : response.resources || {};
        updated[messageIndex].fallbackType = response.fallbackType || null;
        updated[messageIndex].externalResources = response.externalResources || [];
        updated[messageIndex].verifiedResources = useVerifiedResources ? verifiedResources : [];
        updated[messageIndex].isAnimatingAnswer = true;

        const hasResources = !isEmpty(response.resources || null);
        updated[messageIndex].verified = hasResources || useVerifiedResources;
    }
    return updated;
};

export const shouldFetchContentLinks = (response, statusComplete, message) => {
    if (hasVerifiedResources(resolveVerifiedResources(response, message))) {
        return false;
    }
    return response.chatStatus === statusComplete && response.resourceType === 'contentful';
};

export const updateMessageAnimationComplete = (historyChat, messageIndex) => {
    const updated = [...historyChat];
    if (updated[messageIndex]) {
        updated[messageIndex].isAnimatingAnswer = false;
    }
    return updated;
};

export const calculateLoadingContentLinks = (message, contentLinkIds) => {
    if (!message || !contentLinkIds) return false;

    const loadedCount =
        message.additionalResources.length + message.additionalResourcesFailed.length;
    const totalCount = Object.keys(contentLinkIds).length;

    return loadedCount !== totalCount;
};

export const findLastSelectedState = parsedSession => {
    let selectedStateFromStorage = null;
    for (let i = parsedSession.length - 1; i >= 0; i -= 1) {
        if (parsedSession[i].selectedState) {
            selectedStateFromStorage = parsedSession[i].selectedState;
            break;
        }
    }
    return selectedStateFromStorage;
};

export const normalizeHistoryMessages = parsedSession => {
    return parsedSession.map((msg, index) => {
        // Skip state label messages
        if (msg.type === 'stateLabel' || msg.type === 'endConversation' || msg.selectedState) {
            return { ...msg, id: msg.id || `state-${index}` };
        }

        // Normalize assistant messages
        return {
            ...msg,
            id: msg.id || msg.chatMessageId || `msg-${index}`,
            additionalResources: Array.isArray(msg.additionalResources)
                ? msg.additionalResources
                : [],
            additionalResourcesFailed: Array.isArray(msg.additionalResourcesFailed)
                ? msg.additionalResourcesFailed
                : [],
            externalResources: Array.isArray(msg.externalResources) ? msg.externalResources : [],
            verifiedResources: Array.isArray(msg.verifiedResources) ? msg.verifiedResources : [],
            chatResources: hasVerifiedResources(msg.verifiedResources)
                ? {}
                : msg.chatResources || {},
            resources: hasVerifiedResources(msg.verifiedResources) ? {} : msg.resources || {},
            verified:
                !isEmpty(msg.additionalResources || []) ||
                hasVerifiedResources(msg.verifiedResources),
            isAnimatingAnswer: false,
        };
    });
};

export const sortStatesWithFederal = (states, federalOption) => {
    const sortedStates = [...states].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
    });

    if (sortedStates.every(item => item.name !== 'Federal')) {
        return [federalOption, ...sortedStates];
    }
    return sortedStates;
};

export const mapStatesToOptions = statesWithFederal => {
    return statesWithFederal.map(state => {
        const { abbreviation, name } = state;
        return { value: abbreviation, label: name };
    });
};

export const extractCurrentChatTitle = historyChat => {
    // Find the last state label index
    const lastStateLabelIndex = historyChat
        .map((msg, index) => (msg.type === 'stateLabel' ? index : -1))
        .filter(index => index !== -1)
        .pop();

    // Get messages after the last state label (or all messages if no state label)
    const messagesAfterStateChange =
        lastStateLabelIndex !== undefined
            ? historyChat.slice(lastStateLabelIndex + 1)
            : historyChat;

    // Find the first user message in this subset
    const firstUserMessage = messagesAfterStateChange.find(msg => msg.type === 'user' && msg.query);
    return firstUserMessage ? firstUserMessage.query : null;
};

export const prepareFeedbackModalConfig = (message, handlers, defaultColors) => {
    return {
        title: 'Feedback',
        handlers,
        showTitle: true,
        description: '',
        itemData: message,
        justifyActions: 'flex-end',
        newModalStyles: true,
        ModalProps: {
            ContentProps: {
                style: {
                    maxWidth: '560px',
                    margin: '1rem',
                    padding: '2.5rem',
                    borderRadius: '0.5rem',
                    backgroundColor: defaultColors.WHITE,
                },
            },
        },
    };
};

export const shouldPollForAnswer = chatStatus => {
    const POLLING_STATUSES = ['OPEN', 'IN_PROCESS', 'PARTIAL_UPDATE'];
    return POLLING_STATUSES.includes(chatStatus);
};

export const isPollingComplete = chatStatus => {
    return chatStatus === 'COMPLETE' || chatStatus === 'ERROR';
};

export const preparePendoEventWithIds = (pendoEvent, chatMessageId, chatSessionId) => {
    if (chatMessageId && chatSessionId) {
        return {
            ...pendoEvent,
            eventProperties: {
                ...pendoEvent.eventProperties,
                chatMessageId,
                chatSessionId,
            },
        };
    }
    return pendoEvent;
};

export const getActiveChatId = (historyChat, getChatSessionIdFn) => {
    return historyChat.length > 0 ? getChatSessionIdFn() : null;
};

export const createHandlersObject = (
    fetchEmployeeData,
    clickReviewCompanyLinkFn,
    sendPendoEvent
) => {
    return {
        fetchEmployeeCountsByState: fetchEmployeeData,
        clickReviewCompanyLink: () => {
            clickReviewCompanyLinkFn(sendPendoEvent);
        },
    };
};

export const shouldCloseSession = previousStateValue => {
    return !isEmpty(previousStateValue);
};

export const calculateMessageIndex = currentHistoryChatLength => {
    return currentHistoryChatLength + 1;
};

export const loadAndNormalizeLocalStorageSession = () => {
    const localStorageSession = localStorage.getItem('ai_chat_session');
    if (isEmpty(localStorageSession)) {
        return null;
    }

    try {
        const parsedSession = JSON.parse(localStorageSession);
        if (parsedSession && parsedSession.length > 0) {
            return {
                selectedState: findLastSelectedState(parsedSession),
                normalizedHistory: normalizeHistoryMessages(parsedSession),
            };
        }
    } catch (error) {
        // Error parsing localStorage data
    }
    return null;
};

export const shouldFetchContentLinksForMessage = msg => {
    if (hasVerifiedResources(msg.verifiedResources)) {
        return false;
    }

    return (
        msg.resourceType === 'contentful' &&
        msg.chatStatus === 'COMPLETE' &&
        msg.resources &&
        typeof msg.resources === 'object' &&
        !Array.isArray(msg.resources) &&
        Object.keys(msg.resources).length > 0 &&
        Array.isArray(msg.additionalResources) &&
        msg.additionalResources.length === 0
    );
};

/**
 * Check if any messages in an array need to load content links (lazy loading)
 * Matches fetchInitContentLinks criteria exactly to avoid unnecessary calls
 * This is a standalone function that doesn't depend on other utility functions
 * to avoid issues with mocked functions in tests.
 *
 * @param {Array} messages - Array of message objects
 * @return {boolean} - True if any message needs content links loaded
 */
export const shouldLoadContentLinksForMessages = messages => {
    if (!messages || !Array.isArray(messages)) {
        return false;
    }

    return messages.some(msg => {
        if (!msg) return false;

        if (hasVerifiedResources(msg.verifiedResources)) {
            return false;
        }

        // Check if message has resources (must be non-empty object, not array)
        const hasResources =
            msg.resources &&
            typeof msg.resources === 'object' &&
            !Array.isArray(msg.resources) &&
            Object.keys(msg.resources).length > 0;

        // Check if additionalResources is empty array (needs loading)
        const needsLinks =
            Array.isArray(msg.additionalResources) && msg.additionalResources.length === 0;

        // Check if not already failed (avoid retries)
        const notFailed =
            !msg.additionalResourcesFailed || msg.additionalResourcesFailed.length === 0;

        // Check if message is complete
        const isComplete = msg.chatStatus === 'COMPLETE';

        // Check if contentful (must match fetchInitContentLinks criteria exactly)
        const isContentful = msg.resourceType === 'contentful';

        return hasResources && needsLinks && notFailed && isComplete && isContentful;
    });
};

export const groupMessagesForHistoryPure = (messages, sessionId) => {
    // Defensive: return empty array if messages is not valid
    if (!messages || !Array.isArray(messages)) {
        return [];
    }

    const groupedMessages = [];

    // Defensive: safely filter messages
    const filteredMessages = messages.filter(
        msg => msg && (msg.type === 'user' || msg.type === 'assistant')
    );

    for (let i = 0; i < filteredMessages.length; i += 1) {
        const msg = filteredMessages[i];

        if (msg.type === 'user') {
            const nextMsg = filteredMessages[i + 1];
            if (nextMsg && nextMsg.type === 'assistant') {
                // Skip message pairs that belong to a different session
                if (
                    sessionId &&
                    nextMsg.chatSessionId &&
                    String(nextMsg.chatSessionId) !== String(sessionId)
                ) {
                    i += 1;
                    continue; // eslint-disable-line no-continue
                }

                const messageData = {
                    query: msg.query || '',
                    answer: nextMsg.answer || '',
                    id: nextMsg.chatMessageId || nextMsg.id || `generated-${Date.now()}-${i}`,
                    chatSessionId: sessionId || 'unknown',
                    resources: Array.isArray(nextMsg.resources) ? nextMsg.resources : [],
                    additionalResources: Array.isArray(nextMsg.additionalResources)
                        ? nextMsg.additionalResources
                        : [],
                    additionalResourcesFailed: Array.isArray(nextMsg.additionalResourcesFailed)
                        ? nextMsg.additionalResourcesFailed
                        : [],
                    externalResources: Array.isArray(nextMsg.externalResources)
                        ? nextMsg.externalResources
                        : [],
                    verifiedResources: Array.isArray(nextMsg.verifiedResources)
                        ? nextMsg.verifiedResources
                        : [],
                    hasError: nextMsg.hasError || false,
                    fallbackType: nextMsg.fallbackType || null,
                };

                // Add errorMessage separately if it exists and is a string
                if (nextMsg.errorMessage && typeof nextMsg.errorMessage === 'string') {
                    messageData.errorMessage = nextMsg.errorMessage;
                }

                groupedMessages.push(messageData);
                i += 1;
            } else {
                // User message without answer yet
                groupedMessages.push({
                    query: msg.query || '',
                    answer: '',
                    id: msg.chatMessageId || msg.id || `generated-${Date.now()}-${i}`,
                    chatSessionId: sessionId || 'unknown',
                    resources: [],
                    additionalResources: [],
                    additionalResourcesFailed: [],
                    externalResources: Array.isArray(msg.externalResources)
                        ? msg.externalResources
                        : [],
                    verifiedResources: Array.isArray(msg.verifiedResources)
                        ? msg.verifiedResources
                        : [],
                });
            }
        }
    }

    return groupedMessages;
};
